<template>
    <DForm
        @submit.prevent="submitForm"
    >
        <div>
            <div class="d-flex justify-content-between gap-3">
                <DCol
                    sm="6"
                    class="pb-3"
                >
                    <DFormRow
                        :label="__('settings.lov_incidents.form.incident_type')"
                        :is-required="true"
                        :error="errors.name"
                        :hint="__('settings.lov_incidents.form.incident_type_hint')"
                    >
                        <DInput
                            id="name"
                            v-model="form.name"
                            :placeholder="__('settings.lov_incidents.form.name')"
                            :is-valid="isFormFieldValid('name')"
                            maxlength="255"
                        />
                    </DFormRow>
                </DCol>
                <DCol
                    sm="6"
                    class="pb-3"
                >
                    <DFormRow
                        :label="__('settings.lov_incidents.form.active')"
                        :is-required="true"
                        :error="errors.incident_type"
                        :hint="__('settings.lov_incidents.form.active_hint')"
                    >
                        <DSwitch
                            id="active"
                            v-model="form.active"
                            color="success"
                            variant="3d"
                            inline
                        />
                    </DFormRow>
                </DCol>
            </div>
            <div class="d-flex justify-content-between gap-3">
                <DCol sm="6">
                    <DCard
                        :title="__('settings.lov_incidents.form.customer_request')"
                        :subtitle="__('settings.lov_incidents.form.customer_request_hint')"
                        padding
                    >
                        <div class="d-flex justify-content-between align-items-center gap-3 p-1 header-section">
                            <span class="required">{{ __('settings.lov_incidents.form.name') }}</span>
                        </div>
                        <div
                            v-for="(item, index) in form.business_incident_customer_request"
                            :key="index"
                        >
                            <div class="d-flex justify-content-between align-items-center gap-2 p-1">
                                <DInput
                                    :id="`business_incident_customer_request_name_${index}`"
                                    v-model="item.name"
                                    :placeholder="__('settings.lov_incidents.form.name')"
                                    :is-valid="isFormFieldValid(`business_incident_customer_request.${index}.name`)"
                                    maxlength="255"
                                />
                                <DSwitch
                                    :id="`business_incident_customer_request_active_${index}`"
                                    v-model="item.active"
                                    color="success"
                                    variant="3d"
                                    inline
                                />
                                <DButton
                                    v-if="item.id === null || !item.is_used"
                                    :id="`business_incident_customer_request_delete_${index}`"
                                    icon="trash-can-outline"
                                    mini
                                    @click="deleteRow(item, form.business_incident_customer_request)"
                                />
                            </div>
                            <p
                                v-if="errors[`business_incident_customer_request.${index}.name`]"
                                class="error-msg"
                            >
                                {{ errors[`business_incident_customer_request.${index}.name`] }}
                            </p>
                        </div>
                    </DCard>
                </DCol>
                <DCol sm="6">
                    <DCard
                        :title="__('settings.lov_incidents.form.incident_solution')"
                        :subtitle="__('settings.lov_incidents.form.incident_solution_hint')"
                        padding
                    >
                        <div class="d-flex justify-content-between align-items-center gap-3 p-1 header-section">
                            <span class="required">{{ __('settings.lov_incidents.form.name') }}</span>
                        </div>
                        <div
                            v-for="(item, index) in form.business_incident_solution"
                            :key="index"
                        >
                            <div class="d-flex justify-content-between align-items-center gap-2 p-1">
                                <DInput
                                    :id="`business_incident_solution_name_${index}`"
                                    v-model="item.name"
                                    :placeholder="__('settings.lov_incidents.form.name')"
                                    :is-valid="isFormFieldValid(`business_incident_solution.${index}.name`)"
                                    maxlength="255"
                                />
                                <DSwitch
                                    :id="`business_incident_solution_active_${index}`"
                                    v-model="item.active"
                                    color="success"
                                    variant="3d"
                                    inline
                                />
                                <DButton
                                    v-if="item.id === null || !item.is_used"
                                    :id="`business_incident_solution_delete_${index}`"
                                    icon="trash-can-outline"
                                    mini
                                    @click="deleteRow(item, form.business_incident_solution)"
                                />
                            </div>
                            <p
                                v-if="errors[`business_incident_solution.${index}.name`]"
                                class="error-msg"
                            >
                                {{ errors[`business_incident_solution.${index}.name`] }}
                            </p>
                        </div>
                    </DCard>
                </DCol>
            </div>
        </div>
        <DFormControls
            :dirty="form.isDirty"
            @submit="submitForm"
        >
            <template #additional_button_before_submit>
                <DFab
                    id="add_customer_request"
                    :color="'secondary'"
                    icon="plus"
                    :label="__('settings.lov_incidents.form.add_customer_request')"
                    @click="addCustomerRequest()"
                />
                <DFab
                    id="add_incident_solution"
                    :color="'secondary'"
                    icon="plus"
                    :label="__('settings.lov_incidents.form.add_incident_solution')"
                    @click="addIncidentSolution()"
                />
            </template>
        </DFormControls>
    </DForm>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3'

const props = defineProps({
    incidentValues: {
        type: Object,
        default () {
            return {}
        }
    },
    errors: {
        type: Object,
        default: () => ({})
    }
})

const emit = defineEmits(['form-submitted'])

const form = useForm({
    id: props.incidentValues.id ?? null,
    name: props.incidentValues.name ?? null,
    active: props.incidentValues.active ?? true,
    business_incident_customer_request: props.incidentValues.business_incident_customer_request ?? [],
    business_incident_solution: props.incidentValues.business_incident_solution ?? []
})

function addCustomerRequest () {
    const newEmptyRow = getNewRow()
    form.business_incident_customer_request.push(newEmptyRow)
}

function addIncidentSolution () {
    const newEmptyRow = getNewRow()
    form.business_incident_solution.push(newEmptyRow)
}

function getNewRow () {
    return {
        id: null,
        name: null,
        active: true,
        business_incident_type_id: null
    }
}

function deleteRow (item, entities) {
    entities.splice(entities.indexOf(item), 1)
}

const submitForm = () => {
    emit('form-submitted', form)
}

</script>

<style lang="scss">
.width-name {
    width: 40%
}
.header-section span {
    font-weight: bold;
}
.error-msg {
    margin: 0 0 2px 5px;
    font-size: 12px;
    color: var(--colorDanger);
}
</style>
